import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Building2, User, LogOut, Menu, X } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useAnalytics } from '@/hooks/useAnalytics'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'

interface NavigationItem {
  name: string
  href: string
  current?: boolean
}

export function NavigationHeader() {
  const { user, logout } = useAuth()
  const { trackButtonClick, trackUserAction } = useAnalytics()
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Helper function to get user display name with fallbacks
  const getUserDisplayName = () => {
    if (!user) return 'User'
    
    // If we have both first and last name
    if (user.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`.trim()
    }
    
    // If we only have first name
    if (user.given_name) {
      return user.given_name
    }
    
    // If we only have last name
    if (user.family_name) {
      return user.family_name
    }
    
    // Fallback to email prefix
    if (user.email) {
      const emailPrefix = user.email.split('@')[0]
      return emailPrefix.split('.')[0] || emailPrefix
    }
    
    // Final fallback
    return 'User'
  }

  const handleLogout = async () => {
    try {
      // Track logout button click
      trackButtonClick('nav-logout-button', 'Logout', {
        button_location: 'navigation_header'
      });

      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const toggleMobileMenu = () => {
    const willOpen = !isMobileMenuOpen;

    trackUserAction('mobile_menu_toggle', {
      action: willOpen ? 'open' : 'close',
      menu_location: 'navigation_header'
    });

    setIsMobileMenuOpen(willOpen)
  }

  const closeMobileMenu = () => {
    trackUserAction('mobile_menu_close', {
      action: 'close',
      menu_location: 'navigation_header'
    });

    setIsMobileMenuOpen(false)
  }

  const handleNavClick = (item: NavigationItem) => {
    trackButtonClick(`nav-${item.name.toLowerCase()}-link`, item.name, {
      button_location: 'navigation_header',
      destination: item.href,
      current_page: location.pathname
    });

    // Close mobile menu if it's open
    if (isMobileMenuOpen) {
      closeMobileMenu();
    }
  }

  const navigationItems: NavigationItem[] = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      current: location.pathname === '/dashboard'
    },
    {
      name: 'Analyze',
      href: '/analyze',
      current: location.pathname === '/analyze'
    },
    {
      name: 'Portfolio',
      href: '/portfolio',
      current: location.pathname === '/portfolio'
    },
    {
      name: 'Risk Assessment',
      href: '/risk-assessment',
      current: location.pathname === '/risk-assessment'
    }
  ]

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <Link
              to="/dashboard"
              className="flex items-center hover:opacity-80 transition-opacity"
              onClick={() => trackButtonClick('nav-logo-link', 'Logo', {
                button_location: 'navigation_header',
                destination: '/dashboard',
                current_page: location.pathname
              })}
            >
              <Building2 className="w-8 h-8 text-primary-600 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">
                Real Estate Analytics
              </h1>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <div className="flex space-x-6">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'px-3 py-2 text-sm font-medium transition-colors',
                    item.current
                      ? 'text-primary-600 border-b-2 border-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                  onClick={() => handleNavClick(item)}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Desktop User Menu */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <User className="w-5 h-5 text-gray-500" />
                <span className="text-sm text-gray-700">
                  {getUserDisplayName()}
                </span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleLogout}
                className="flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              onClick={toggleMobileMenu}
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40 md:hidden"
            onClick={closeMobileMenu}
          />
          
          {/* Mobile menu panel */}
          <div className="absolute top-16 inset-x-0 bg-white border-b border-gray-200 shadow-lg z-50 md:hidden">
            <div className="px-4 pt-2 pb-3 space-y-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'block px-3 py-3 rounded-md text-base font-medium transition-colors',
                    item.current
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  )}
                  onClick={() => handleNavClick(item)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            
            {/* Mobile user menu */}
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="flex items-center px-4 space-x-3">
                <div className="flex-shrink-0">
                  <User className="w-8 h-8 text-gray-400" />
                </div>
                <div>
                  <div className="text-base font-medium text-gray-800">
                    {getUserDisplayName()}
                  </div>
                  <div className="text-sm text-gray-500">
                    {user?.email}
                  </div>
                </div>
              </div>
              <div className="mt-3 px-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  )
}